/* Tela de login magic link — 2 fases: e-mail → código 6 dígitos */
import React, { useState, useRef, forwardRef } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { GlowText } from './GlowText';
import { GoogleLoginButton } from './GoogleLoginButton';
import type { Locale } from '../types';

interface LoginViewProps {
  onLogin: (email: string, code: string) => Promise<void>;
  onRequestCode: (email: string, locale: Locale) => Promise<void>;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Erro pré-existente (ex.: retorno do login Google com #error=) */
  initialError?: string;
}

/** Input retro com forwardRef — obrigatório por design system */
const RetroInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }
>(({ label, hint, error, id, ...props }, ref) => {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div style={{ marginBottom: '20px' }}>
      <label
        htmlFor={inputId}
        style={{
          display: 'block',
          fontFamily: 'var(--font-sans)',
          fontSize: '12px',
          color: 'var(--ink-mute)',
          marginBottom: '6px',
          letterSpacing: '0.02em',
        }}
      >
        <GlowText>{label}</GlowText>
      </label>
      <input
        ref={ref}
        id={inputId}
        {...props}
        style={{
          width: '100%',
          padding: '10px 14px',
          background: 'var(--ink-soft)',
          border: `1px solid ${error ? 'var(--coral)' : 'var(--ink-mute)'}`,
          color: 'var(--ink-light)',
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          outline: 'none',
          letterSpacing: '0.04em',
          boxSizing: 'border-box',
          ...props.style,
        }}
        aria-describedby={hint || error ? `${inputId}-hint` : undefined}
        aria-invalid={!!error}
      />
      {(hint || error) && (
        <p
          id={`${inputId}-hint`}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '11px',
            color: error ? 'var(--coral)' : 'var(--ink-mute)',
            marginTop: '5px',
          }}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
RetroInput.displayName = 'RetroInput';

/** Botão terminal retro */
function RetroButton({
  children,
  loading,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...props}
      disabled={props.disabled ?? loading}
      style={{
        width: '100%',
        padding: '12px',
        background: loading ? 'var(--ink-soft)' : 'var(--cta-blue)',
        border: 'none',
        color: loading ? 'var(--ink-mute)' : '#FFFFFF',
        fontFamily: 'var(--font-pixel)',
        fontSize: '13px',
        fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer',
        letterSpacing: '0.03em',
        marginTop: '4px',
        transition: 'background 180ms, box-shadow 180ms',
        boxShadow: loading ? 'none' : 'var(--coral-glow-dim)',
        ...props.style,
      }}
    >
      {loading ? '...' : children}
    </button>
  );
}

export function LoginView({
  onLogin,
  onRequestCode,
  locale,
  onLocaleChange,
  t,
  initialError,
}: LoginViewProps) {
  // Fase 1: captura e-mail; Fase 2: captura código
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  // initialError: mensagem amigável do retorno Google (#error=...) — some
  // assim que o médico interage (setError('') nos onChange).
  const [error, setError] = useState(initialError ?? '');
  const [success, setSuccess] = useState('');

  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  // Fase 1 — solicita código
  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError(t('error.invalidEmail')); return; }
    setError('');
    setLoading(true);
    try {
      await onRequestCode(email.trim(), locale);
      setSuccess(t('login.codeSent', { email: email.trim() }));
      setPhase('code');
      // Foca no input de código automaticamente
      setTimeout(() => codeRef.current?.focus(), 100);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('error.500');
      // Trata rate limit
      setError(msg.toLowerCase().includes('rate') ? t('error.tooManyRequests') : msg);
    } finally {
      setLoading(false);
    }
  }

  // Fase 2 — verifica código e faz login
  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || code.length !== 6) {
      setError(t('error.invalidCode'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onLogin(email.trim(), code.trim());
      // Sucesso — App.tsx vai detectar JWT e mudar para ChatView
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('error.invalidCode');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--ink)',
      }}
    >
      <Header
        locale={locale}
        onLocaleChange={onLocaleChange}
        onLogout={() => {}}
        creditsInfo={null}
        t={t}
        showUserControls={false}
      />

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '460px',
            border: '1px solid var(--violet)',
            boxShadow: 'var(--coral-glow-md)',
            padding: '36px 32px',
            background: 'var(--ink-soft)',
          }}
        >
          {/* Cabeçalho do card */}
          <div style={{ marginBottom: '28px', textAlign: 'center' }}>
            <div
              className="glow-title"
              style={{
                fontFamily: 'var(--font-pixel)',
                fontSize: '15px',
                fontWeight: 700,
                color: '#FFFFFF',
                marginBottom: '8px',
                letterSpacing: '0.02em',
              }}
            >
              <GlowText>{t('login.title')}</GlowText>
            </div>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                color: 'var(--ink-light)',
                marginBottom: '6px',
              }}
            >
              <GlowText>{t('login.subtitle')}</GlowText>
            </div>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                color: 'var(--ink-mute)',
              }}
            >
              <GlowText>{t('login.description')}</GlowText>
            </div>
          </div>

          {/* Mensagem de sucesso (código enviado) */}
          {success && (
            <div
              role="status"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                color: 'var(--green-fluor)',
                border: '1px solid var(--green-fluor)',
                padding: '10px 14px',
                marginBottom: '20px',
                boxShadow: 'var(--green-glow-sm)',
              }}
            >
              ✓ {success}
            </div>
          )}

          {/* Mensagem de erro */}
          {error && (
            <div
              role="alert"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                color: 'var(--coral)',
                border: '1px solid var(--coral)',
                padding: '10px 14px',
                marginBottom: '20px',
              }}
            >
              ✗ {error}
            </div>
          )}

          {/* Fase 1 — e-mail */}
          {phase === 'email' && (
            <form onSubmit={handleRequestCode} noValidate>
              <RetroInput
                ref={emailRef}
                id="login-email"
                label={t('login.email.label')}
                type="email"
                placeholder={t('login.email.placeholder')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                autoComplete="email"
                autoFocus
                required
                disabled={loading}
              />
              <RetroButton type="submit" loading={loading}>
                {loading ? t('login.sending') : t('login.cta.requestCode')}
              </RetroButton>
            </form>
          )}

          {/* Chip "Continuar com Google" — renderiza null se o backend está
              dormante (sem secrets). Caminho paralelo ao magic link. */}
          {phase === 'email' && <GoogleLoginButton locale={locale} t={t} />}

          {/* Fase 2 — código */}
          {phase === 'code' && (
            <form onSubmit={handleVerify} noValidate>
              <RetroInput
                ref={codeRef}
                id="login-code"
                label={t('login.code.label')}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder={t('login.code.placeholder')}
                value={code}
                onChange={(e) => {
                  // Aceita apenas dígitos
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError('');
                }}
                hint={t('login.code.hint')}
                autoComplete="one-time-code"
                disabled={loading}
                style={{ fontSize: '20px', letterSpacing: '0.4em', textAlign: 'center' }}
              />
              <RetroButton type="submit" loading={loading}>
                {loading ? t('login.verifying') : t('login.cta.verify')}
              </RetroButton>
              <button
                type="button"
                onClick={() => {
                  setPhase('email');
                  setCode('');
                  setError('');
                  setSuccess('');
                  setTimeout(() => emailRef.current?.focus(), 100);
                }}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--ink-mute)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}
              >
                {t('login.cta.back')}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer t={t} />
    </div>
  );
}
